import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Drawer } from "antd";
import { useRateCardsActions } from "tap/store/rateCards/useRateCards.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { tapApi } from "tap/api.js";
import { useEditRateCardForm } from "./hooks/useEditRateCardForm";
import {
  validateRateCardForm,
  getPercentagesEnabled,
} from "./utils/validation";
import { buildEditRateCardPayload } from "./utils/payloadBuilder";
import BasicInfoForm from "./rate-card-form/basic-info";
import TiersForm from "./rate-card-form/tiers";
import { RateCardDrawerFooter } from "./components/RateCardDrawerFooter";
import "./styles.scss";

/**
 * EditRateCardDrawer component for editing existing rate cards.
 */
export function EditRateCardDrawer() {
  const [validationErrors, setValidationErrors] = useState({});

  const { isEditDrawerOpen, selectedRateCard } = useSelector(
    (state) => state.rateCards,
  );
  const { updateRateCards, clearRateCardForm } = useRateCardsActions();
  const { showSuccessToast, showErrorToast } = useToasts();
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const {
    formData,
    tierFormats,
    currencyOptions,
    formatOptions,
    windowSequenceOptions,
    priceTypeOptions,
    revShareOptions,
    handleInputChange,
    handleAddTierFormat,
    handleRemoveTierFormat,
    handleTierFormatChange,
    handleAddTierRow,
    handleRemoveTierRow,
    handleTierRowInputChange,
    showTierFormat,
    priceType,
  } = useEditRateCardForm();

  const handleClose = () => {
    setValidationErrors({});
    updateRateCards({ isEditDrawerOpen: false });
  };

  const handleAfterOpenChange = useCallback(
    (open) => {
      if (open) return;
      clearRateCardForm();
    },
    [clearRateCardForm],
  );

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const handleSave = async () => {
    const percentagesEnabled = getPercentagesEnabled(formData, false);
    const { isValid, errors } = validateRateCardForm(
      formData,
      "edit",
      percentagesEnabled,
    );

    if (!isValid) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});

    const payload = buildEditRateCardPayload(selectedRateCard);

    setLoadingTrue();
    try {
      await tapApi.saveRateCard(payload);
      showSuccessToast("Rate Card saved successfully.");
      updateRateCards({ refreshRateCards: true });
      handleClose();
    } catch (error) {
      showErrorToast(error?.message || "Error saving Rate Card.");
      console.error("Error saving rate card:", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <Drawer
      className="create-drawer"
      title="Edit Rate Card"
      width="95vw"
      placement="right"
      open={isEditDrawerOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <RateCardDrawerFooter
          hasValidationErrors={hasValidationErrors}
          onClose={handleClose}
          onSave={handleSave}
        />
      }
    >
      <div className="rate-card-form-container">
        <BasicInfoForm
          formData={formData}
          mode="edit"
          handleInputChange={handleInputChange}
          currencyOptions={currencyOptions}
          windowSequenceOptions={windowSequenceOptions}
          priceTypeOptions={priceTypeOptions}
          revShareOptions={revShareOptions}
          validationErrors={validationErrors}
        />
        <div className="rate-card-form-tiers-section" style={{ marginTop: 32 }}>
          <TiersForm
            formData={formData}
            mode="edit"
            tierFormats={tierFormats}
            formatOptions={formatOptions}
            handleTierFormatChange={handleTierFormatChange}
            handleInputChange={handleInputChange}
            handleAddTierFormat={handleAddTierFormat}
            handleRemoveTierFormat={handleRemoveTierFormat}
            handleTierRowInputChange={handleTierRowInputChange}
            handleAddTierRow={handleAddTierRow}
            handleRemoveTierRow={handleRemoveTierRow}
            validationErrors={validationErrors}
            showTierRows={showTierFormat && !!priceType}
          />
        </div>
      </div>
    </Drawer>
  );
}
